/**
 * Application Entry Point
 * 
 * Boots the Pivot.is client and initializes the main application.
 */

// ============================================================================
// CRITICAL: Early tracing system - must execute BEFORE any imports
// This detects infinite loops, hangs, and execution bottlenecks
// ============================================================================

const TRACE_START = performance.now();
const TRACE_STEPS: Array<{ name: string; time: number; delta: number }> = [];

function traceStep(name: string): void {
  const now = performance.now();
  const delta = TRACE_STEPS.length > 0 ? now - TRACE_STEPS[TRACE_STEPS.length - 1].time : now - TRACE_START;
  TRACE_STEPS.push({ name, time: now, delta });

  const stack = new Error().stack;
  const stackLine = stack?.split('\n')[2]?.trim() || 'unknown';

  // Detect hangs (>5 seconds between steps)
  if (delta > 5000) {
  }
}

function traceError(location: string, error: unknown): void {
  const now = performance.now();
  if (error instanceof Error) {
  }
}

// Global hang detector - fires after 10 seconds
const HANG_TIMEOUT = setTimeout(() => {
  TRACE_STEPS.forEach((step, i) => {
  });
}, 10000);

// Initialize Sentry (must be before error handlers)
(async () => {
  try {
    const { initSentry } = await import('./services/sentry/index.js');
    initSentry();
    traceStep('INDEX: Sentry initialized');
    
    // Debug button removed - Sentry integration verified and working
    // To re-enable for testing, uncomment below:
    // if (import.meta.env.DEV) {
    //   const { addSentryDebugButton } = await import('./services/sentry/debug-button.js');
    //   if (document.readyState === 'loading') {
    //     document.addEventListener('DOMContentLoaded', () => {
    //       addSentryDebugButton();
    //     });
    //   } else {
    //     addSentryDebugButton();
    //   }
    // }
  } catch (error) {
    traceError('initializing Sentry', error);
    // Don't throw - Sentry failure shouldn't prevent app from starting
  }
})();

// Suppress ResizeObserver loop warning (harmless browser warning)
// This warning occurs when ResizeObserver callbacks trigger layout changes
// It's a known browser quirk and doesn't indicate a real problem
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;
console.error = (...args: unknown[]) => {
  const message = args[0];
  if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
    // Suppress this specific warning - it's harmless
    return;
  }
  originalConsoleError.apply(console, args);
};
console.warn = (...args: unknown[]) => {
  const message = args[0];
  if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
    // Suppress this specific warning - it's harmless
    return;
  }
  originalConsoleWarn.apply(console, args);
};

// Initialize centralized error handler (will set up its own global handlers)
// This must be done early, before any other code that might throw errors
// Using IIFE to handle async import at top level
(async () => {
  try {
    const { initGlobalErrorHandlers } = await import('./services/error-handler/index.js');
    initGlobalErrorHandlers();
    traceStep('INDEX: Global error handlers initialized');
  } catch (error) {
    traceError('initializing error handlers', error);
    // Don't throw - error handler failure shouldn't prevent app from starting
  }
})();

// Catch unhandled errors and promise rejections (for tracing - error handler will show toasts)
window.addEventListener('error', (event) => {
  // Suppress ResizeObserver loop warnings from error events too
  const message = event.message || (event.error && event.error.message) || '';
  if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
    // Suppress this specific warning - it's harmless
    return;
  }
  traceError('unhandled-error', event.error || event.message);
  const now = performance.now();
});

window.addEventListener('unhandledrejection', (event) => {
  traceError('unhandled-promise-rejection', event.reason);
  const now = performance.now();
});

// Mark initialization complete
function markComplete(): void {
  clearTimeout(HANG_TIMEOUT);
  const total = performance.now() - TRACE_START;
  TRACE_STEPS.forEach((step, i) => {
  });
}

traceStep('INDEX: Script execution started');

// ============================================================================
// END EARLY TRACING - Now proceed with imports
// ============================================================================

traceStep('INDEX: Starting imports');
import { bootClient, restorePersistedProcesses } from './core/boot.js';
traceStep('INDEX: bootClient imported');
import { bus } from './core/events.js';
traceStep('INDEX: bus imported');
import { state, setState, updateState } from './core/state.js';
traceStep('INDEX: state imported');
import { undo, redo } from './core/controller.js';
traceStep('INDEX: controller imported');
import { initApp } from './app/app.js';
traceStep('INDEX: initApp imported');
// All functionality uses the Process interface and is registered via systems registry
traceStep('INDEX: Process system ready');
import { log, trace } from './services/logging/index.js';
traceStep('INDEX: logging service imported');

traceStep('INDEX: Importing process modules for registration');
// Import processes to register them BEFORE bootClient runs (restores persisted processes)
import './processes/spawner/index.js';
import './processes/pathfinder/index.js';
import './processes/microphone/index.js';
import './processes/visualization/index.js';
// Import layout processes
import './processes/layouts/eisenhower.js';
import './processes/layouts/todo-list.js';
import './processes/layouts/calendar.js';
import './processes/layouts/note.js';
// Import system processes
import './processes/auth/index.js';
import './processes/notifications/index.js';
import './processes/settings/index.js';
import './processes/decision-history/index.js';
import './processes/subscription/index.js';
import './processes/pricing/index.js';
import './processes/user-menu/index.js';
import './processes/drawing/index.js';
import './processes/graph-app/index.js';
import './processes/life-graph/index.js';
import './processes/half-height-testing/index.js';
import './processes/calculator/index.js';
import './processes/cortex-details/index.js';
traceStep('INDEX: Process modules imported');

traceStep('INDEX: All imports complete - starting application logic');

// Start application trace
const appTraceId = trace.start('boot-application');

traceStep('INDEX: About to call bootClient()');
try {
  // Boot core system (transports + hydration + command wiring)
  await bootClient();
  traceStep('INDEX: bootClient() completed');
} catch (error) {
  traceError('bootClient()', error);
  // Don't throw - continue with app initialization even if bootClient fails
}

// Request microphone permissions early (required for wake word detection)
traceStep('INDEX: Requesting microphone permissions');
try {
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    // Request microphone permission early so users can say "Hey Pivot" immediately
    // This is a non-blocking request - we don't await it, just trigger it
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        // Stop the stream immediately - we just needed permission
        stream.getTracks().forEach(track => track.stop());
        traceStep('INDEX: Microphone permission granted');
      })
      .catch((error) => {
        traceError('microphone-permission', error);
        // Don't throw - app can still work without microphone (just no wake word)
      });
    traceStep('INDEX: Microphone permission request initiated');
  } else {
    traceStep('INDEX: MediaDevices API not available');
  }
} catch (error) {
  traceError('microphone-permission-request', error);
  // Don't throw - microphone permission is not critical for app startup
}

// Initialize network request tracking (must be early to intercept all requests)
traceStep('INDEX: Initializing network request tracking');
try {
  const { initializeNetworkTracking } = await import('./services/network-tracking/index.js');
  initializeNetworkTracking();
  traceStep('INDEX: Network request tracking initialized');
} catch (error) {
  traceError('network-tracking-init', error);
  // Don't throw - network tracking is not critical
}

// Initialize window focus detection
traceStep('INDEX: Initializing window focus detection');
try {
  const { initializeWindowFocusDetection } = await import('./services/window-focus/index.js');
  initializeWindowFocusDetection();
  traceStep('INDEX: Window focus detection initialized');
} catch (error) {
  traceError('window-focus-init', error);
  // Don't throw - window focus detection is not critical
}

// Minimal shared audio controls
traceStep('INDEX: Initializing audio controls');
try {
  const { mountVolumeSlider } = await import('./services/audio/volume-slider.js');
  mountVolumeSlider();
  traceStep('INDEX: Volume slider mounted');
} catch (error) {
  traceError('volume-slider-init', error);
}

try {
  const { primeAmbientAutostart } = await import('./services/audio/ambient-controller.js');
  primeAmbientAutostart();
  traceStep('INDEX: Ambient autostart armed');
} catch (error) {
  traceError('ambient-autostart-init', error);
}


// Initialize wake word detection
traceStep('INDEX: Initializing wake word detection');
try {
  const { initializeWakeWordDetection } = await import('./services/wake-word/index.js');
  
  // Get user's language preference (from state or browser)
  const userLocale = state.user?.locale || navigator.language?.split('-')[0] || 'en';
  
  await initializeWakeWordDetection({
    language: userLocale, // Auto-detects wake words and interruption phrases for this language
    // wakeWords and interruptionWords are auto-detected from language
    // You can override them if needed:
    // wakeWords: [...],
    // interruptionWords: [...],
    onWakeWordDetected: (wakeWord: string) => {
      
      // Activate microphone when wake word is detected
      bus.emit('microphone.activate.requested', { reason: 'wake_word', timestamp: Date.now() });
      
      // Send the wake word phrase to the LLM as if the user had spoken it
      // This makes the interaction more natural - the AI responds to "Hey Pivot"
      const timestamp = Date.now();
      const wakePayload: any = {
        transcript: wakeWord,
        timestamp,
        source: 'wake_word'
      };
      bus.emit('user.voice', wakePayload);
      
    },
    onInterruptionDetected: (interruptionWord: string) => {
      
      // Stop TTS immediately when user says stop/pause/etc
      bus.emit('tts.stop.requested', { reason: 'user_interruption', word: interruptionWord, timestamp: Date.now() });
      
      // Optionally stop microphone if user says "stop listening" or similar
      if (interruptionWord.toLowerCase().includes('stop') || interruptionWord.toLowerCase().includes('quiet')) {
        bus.emit('microphone.stop.requested', { reason: 'user_interruption', timestamp: Date.now() });
      }
    }
  });
  traceStep('INDEX: Wake word detection initialized');
} catch (error) {
  traceError('wake-word-init', error);
  // Don't throw - wake word detection is not critical
}

traceStep('INDEX: Process system initialized');
// Processes are started via processManager.startProcess(kind, id) when needed
// Processes are registered in the systems registry and loaded on demand

// Initialize AI drawing voice handler (after app is initialized)
// We'll initialize it after initApp() to ensure all systems are ready

// Debugger is now standalone (see app/standalone-debugger/)
// No longer a Process - removed debugger process

traceStep('INDEX: About to set initial user state');
try {
  // Set initial state if first-run
  if (!state.user) {
    setState('user', {
      displayName: 'Guest',
      locale: 'en',
      timezone: 'Europe/Berlin',
    });
  }
  traceStep('INDEX: Initial user state set');
} catch (error) {
  traceError('setting initial user state', error);
  throw error;
}

traceStep('INDEX: About to call initApp() - this is critical path');
try {
  // Initialize main application (grid, animation, rendering, profiles, UI)
  // App should be fully loaded before showing the splash screen overlay
  initApp();
  traceStep('INDEX: initApp() completed');
  
  // Emit app initialized event (for metaball and other systems)
  bus.emit('app.initialized', { timestamp: Date.now() });
  traceStep('INDEX: app.initialized event emitted');

traceStep('INDEX: Restoring persisted processes');
restorePersistedProcesses();
traceStep('INDEX: Persisted process restoration scheduled');
} catch (error) {
  traceError('initApp()', error);
  throw error;
}

// Initialize AI drawing voice handler after app is initialized (async)
(async () => {
  traceStep('INDEX: Initializing AI drawing voice handler');
  try {
    const { initializeDrawingVoiceHandler } = await import('./services/drawing/voice-handler.js');
    initializeDrawingVoiceHandler();
    traceStep('INDEX: AI drawing voice handler initialized');
  } catch (error) {
    traceError('drawing-voice-handler-init', error);
    // Don't throw - drawing voice handler is not critical
  }
})();

// Show loading screen overlay AFTER app is initialized (artistic overlay, not a loader)
// The app is already loaded behind it, this is just a visual effect
traceStep('INDEX: Showing loading screen overlay');
try {
  const { showLoadingScreen } = await import('./services/loading-screen/index.js');
  // Don't await - show it asynchronously so it doesn't block
  showLoadingScreen().catch((error) => {
    traceError('showLoadingScreen()', error);
  });
  traceStep('INDEX: Loading screen overlay started');
} catch (error) {
  traceError('showLoadingScreen()', error);
  // Don't throw - loading screen failure shouldn't prevent app from starting
}

// Initialize application lifecycle (conditional process startup)
traceStep('INDEX: About to initialize app lifecycle');
try {
  // Import process manager
  const { processManager } = await import('./systems/process-manager.js');
  traceStep('INDEX: Process manager imported');

  // CRITICAL: Auto-start microphone process - primary user input stream
  // This is the only core process that starts automatically on app boot
  // The microphone process is essential for the decision-making application
  // as it captures user voice input and emits it as a public event stream
  // NOTE: If microphone was persisted and restored in boot.ts, startProcess will return early
  processManager.startProcess('microphone', 'microphone-main');
  traceStep('INDEX: Microphone process started');

  // Initialize app lifecycle (conditionally starts processes based on app state)
  const { initializeAppLifecycle } = await import('./core/app-lifecycle.js');
  await initializeAppLifecycle();
  traceStep('INDEX: App lifecycle initialized');

  // Initialize URL routing (checks URL and shows appropriate views)
  const { initURLRouting } = await import('./services/routing/url-router.js');
  initURLRouting();
  traceStep('INDEX: URL routing initialized');

  // Auto-start spawner process - view spawning system for testing and demonstration
  // Spawns views at regular intervals with varying policies and behaviors
  // NOTE: Auto-start is disabled - spawner must be started manually via debugger or API
  // processManager.startProcess('spawner', 'spawner-main');
  traceStep('INDEX: Spawner process auto-start disabled');

  // Auto-start visualization process in dev mode - handles visualization signals from Cortex API
  // This process listens for visualization.signal events and renders them as views
  if (import.meta.env.DEV) {
    processManager.startProcess('visualization', 'visualization-main');
    traceStep('INDEX: Visualization process started (dev mode)');
    // Removed console.log - use traceStep for debugging instead
  }
} catch (error) {
  traceError('starting core processes', error);
  // Don't throw - process failures shouldn't prevent app from starting
  // (though it will limit functionality)
}

traceStep('INDEX: About to set up keyboard shortcuts and global API');
try {
  // Import standalone debugger
  let standaloneDebugger: any = null;
  try {
    const debuggerModule = await import('./app/standalone-debugger/index.js');
    standaloneDebugger = debuggerModule.standaloneDebugger;
    traceStep('INDEX: Standalone debugger imported');
  } catch (error) {
    // Only log actual errors, not debug info
    if (error instanceof Error) {
      console.error('[INDEX] Failed to import standalone debugger:', error.message);
    }
    traceError('importing standalone debugger', error);
  }

  // Add keyboard shortcuts
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', async (e) => {
      // Ctrl+D or Cmd+D: Toggle standalone debugger modal
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && !e.shiftKey) {
        e.preventDefault();
        if (standaloneDebugger) {
          standaloneDebugger.toggle();
        } else {
          // Fallback: try to import if not already loaded
          try {
            const { standaloneDebugger: dbg } = await import('./app/standalone-debugger/index.js');
            dbg.toggle();
          } catch (err) {
            console.error('[INDEX] Failed to toggle debugger:', err);
          }
        }
      }

      // Ctrl+Shift+D removed - debugger grid feature removed
      // Enter key page navigation removed - app now uses single horizontal feed

      // F key: Toggle fullscreen
      if ((e.key === 'f' || e.key === 'F') && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        // Only trigger if not typing in an input/textarea
        const target = e.target as HTMLElement;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
          return; // Don't prevent F when typing
        }
        e.preventDefault();
        
        // Check if fullscreen is currently active
        const isFullscreen = !!(
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement ||
          (document as any).msFullscreenElement
        );

        if (isFullscreen) {
          // Exit fullscreen
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if ((document as any).webkitExitFullscreen) {
            (document as any).webkitExitFullscreen();
          } else if ((document as any).mozCancelFullScreen) {
            (document as any).mozCancelFullScreen();
          } else if ((document as any).msExitFullscreen) {
            (document as any).msExitFullscreen();
          }
        } else {
          // Enter fullscreen
          const element = document.documentElement;
          if (element.requestFullscreen) {
            element.requestFullscreen();
          } else if ((element as any).webkitRequestFullscreen) {
            (element as any).webkitRequestFullscreen();
          } else if ((element as any).mozRequestFullScreen) {
            (element as any).mozRequestFullScreen();
          } else if ((element as any).msRequestFullscreen) {
            (element as any).msRequestFullscreen();
          }
        }
      }

      // Spacebar: Used by microphone process for hold-to-activate
      // The microphone process handles spacebar in capture phase, so this global handler
      // only fires if the microphone process isn't active or doesn't intercept it
      // (fallback behavior - microphone process should handle it first)

      // Ctrl+Shift+S or Cmd+Shift+S: Demo all shapes (only on drawing screen)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        const { getDrawingState } = await import('./services/drawing/index.js');
        const drawingState = getDrawingState();
        if (drawingState.active) {
          // Get canvas size from window dimensions
          const canvasSize = {
            width: window.innerWidth,
            height: window.innerHeight,
          };
          const { demoAllShapes } = await import('./services/drawing/ai-drawing.js');
          await demoAllShapes(canvasSize);
        }
      }
    });

    // Expose global API for debugging and integration
    (window as any).pivot = {
      undo,
      redo,
      bus,
      state,
      toggleDebugger: async () => {
        const { standaloneDebugger } = await import('./app/standalone-debugger/index.js');
        standaloneDebugger.toggle();
      },
      log,
      trace,
      // Expose trace steps for debugging
      getTraceSteps: () => TRACE_STEPS,
      getTotalBootTime: () => performance.now() - TRACE_START,
      // Debug flags
      debugAllocation: false, // Set to true to print allocation maps
      // Helper to toggle allocation map debugging
      toggleAllocationMap: () => {
        (window as any).pivot.debugAllocation = !(window as any).pivot.debugAllocation;
      },
      // Helper to start spawner process
      startSpawner: async () => {
        const { processManager } = await import('./systems/process-manager.js');
        return processManager.startProcess('spawner', 'spawner-main');
      },
      // Helper to start calculator process
      startCalculator: async () => {
        const { processManager } = await import('./systems/process-manager.js');
        return processManager.startProcess('calculator', 'calculator-main');
      },
      // Drawing demo function
      demoAllShapes: async () => {
        const { getDrawingState } = await import('./services/drawing/index.js');
        const drawingState = getDrawingState();
        if (drawingState.active) {
          const canvasSize = {
            width: window.innerWidth,
            height: window.innerHeight,
          };
          const { demoAllShapes } = await import('./services/drawing/ai-drawing.js');
          await demoAllShapes(canvasSize);
        } else {
          console.warn('Drawing screen is not active. Switch to drawing screen first.');
        }
      },
    };
  }
  traceStep('INDEX: Keyboard shortcuts and global API set up');
} catch (error) {
  traceError('setting up keyboard shortcuts', error);
  // Don't throw - this is non-critical
}

traceStep('INDEX: Application bootstrap complete');

trace.end(appTraceId);

markComplete();
traceStep('INDEX: Script execution finished');
